import React from "react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { CalendarIcon, ClockIcon, DollarSignIcon, UsersIcon } from "lucide-react"
import { AcademicianButton } from "@/components/academician/academician-button"
import { Link } from "react-router-dom"

export type CohortStatus = "enrolling" | "starting-soon" | "in-progress" | "in_progress" | "completed" | "draft" | string

export interface CohortProgramCardProps {
  /**
   * Cohort name/title
   */
  name: string

  /**
   * Current status of the cohort
   */
  status: CohortStatus

  /**
   * Start date of the cohort
   */
  startDate: string

  /**
   * Duration of the program (e.g., "8 weeks", "12 weeks")
   */
  duration: string

  /**
   * Price of the cohort (e.g., "$499", "$999")
   */
  price: string

  /**
   * Number of enrolled students
   */
  enrolled: number

  /**
   * Maximum capacity
   */
  capacity: number

  /**
   * Optional banner image URL
   */
  bannerImage?: string

  /**
   * Link to cohort details page
   */
  href?: string

  /**
   * URL for the cohort logo image
   */
  logoUrl?: string | null

  /**
   * Whether this cohort is private
   */
  isPrivate?: boolean

  /**
   * Whether to show the price
   */
  showPrice?: boolean

  /**
   * Whether to show the View Details button/link
   */
  showDetailsLink?: boolean

  /**
   * Click handler for the card
   */
  onClick?: () => void

  /**
   * Additional CSS classes
   */
  className?: string
}

const getStatusConfig = (status: CohortStatus) => {
  switch (status) {
    case "enrolling":
      return {
        label: "Enrolling",
        variant: "default" as const,
        className: "bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700"
      }
    case "draft":
      return {
        label: "Draft",
        variant: "secondary" as const,
        className: "bg-gray-400 hover:bg-gray-500 text-white dark:bg-gray-400 dark:hover:bg-gray-500"
      }
    case "starting-soon":
      return {
        label: "Starting Soon",
        variant: "default" as const,
        className: "bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-600 dark:hover:bg-amber-700"
      }
    case "in-progress":
    case "in_progress":
      return {
        label: "In Progress",
        variant: "default" as const,
        className: "bg-primary hover:bg-primary/90 text-primary-foreground dark:bg-primary dark:hover:bg-primary/90"
      }
    case "completed":
      return {
        label: "Completed",
        variant: "secondary" as const,
        className: "bg-gray-600 hover:bg-gray-700 text-white dark:bg-gray-600 dark:hover:bg-gray-700"
      }
    default:
      return {
        label: status,
        variant: "secondary" as const,
        className: "bg-gray-400 hover:bg-gray-500 text-white dark:bg-gray-400 dark:hover:bg-gray-500"
      }
  }
}

const getCTAButton = (status: CohortStatus) => {
  switch (status) {
    case "enrolling":
      return "Enroll Now"
    case "starting-soon":
      return "Reserve Spot"
    case "in-progress":
    case "in_progress":
      return "View Details"
    case "completed":
      return "View Details"
    default:
      return "View Details"
  }
}

export const CohortProgramCard: React.FC<CohortProgramCardProps> = ({
  name,
  status,
  startDate,
  duration,
  price,
  enrolled,
  capacity,
  bannerImage,
  href,
  logoUrl,
  isPrivate = false,
  showPrice = true,
  showDetailsLink = true,
  onClick,
  className
}) => {
  const statusConfig = getStatusConfig(status)
  const ctaText = getCTAButton(status)
  const isFull = enrolled >= capacity
  const enrollmentPercentage = (enrolled / capacity) * 100

  const cardContent = (
    <div
      className={cn(
        "bg-card border border-border rounded-lg overflow-hidden transition-all duration-200",
        "hover:shadow-lg hover:-translate-y-1",
        (href || onClick) && "cursor-pointer",
        className
      )}
      onClick={onClick}
    >
      {/* Banner Image */}
      {bannerImage && (
        <div className="aspect-video w-full overflow-hidden bg-muted">
          <img
            src={bannerImage}
            alt={name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Content Area */}
      <div className="p-6 space-y-4">
        {/* Header with Title and Status Badge */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt={name} className="w-8 h-8 rounded-lg shrink-0 object-cover" />
            ) : (
              <img src="/logo.png" alt="Academician Startup" className="w-8 h-8 rounded-lg shrink-0 object-cover" />
            )}
            <h4 className="text-lg font-semibold text-foreground leading-tight flex-1 min-w-0">
              {name}
            </h4>
          </div>
          <div className="flex flex-col items-center gap-1 shrink-0">
            <Badge
              variant={statusConfig.variant}
              className={cn("shrink-0", statusConfig.className)}
            >
              {statusConfig.label}
            </Badge>
            {isPrivate && (
              <span className="text-xs font-semibold text-red-500">Private</span>
            )}
          </div>
        </div>

        {/* Details Grid */}
        <div className="space-y-3">
          {/* Start Date */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarIcon className="h-4 w-4 shrink-0" />
            <span>{startDate}</span>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ClockIcon className="h-4 w-4 shrink-0" />
            <span>{duration}</span>
          </div>

          {/* Price */}
          {showPrice && (
            <div className="flex items-center gap-2 text-sm text-foreground font-medium">
              <DollarSignIcon className="h-4 w-4 shrink-0" />
              <span>{price}</span>
            </div>
          )}

          {/* Enrollment Count */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <UsersIcon className="h-4 w-4 shrink-0" />
                <span>Enrolled</span>
              </div>
              <span className={cn(
                "font-medium",
                isFull ? "text-amber-600 dark:text-amber-500" : "text-foreground"
              )}>
                {enrolled}/{capacity} spots
              </span>
            </div>
            
            {/* Enrollment Progress Bar */}
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className={cn(
                  "h-full transition-all duration-300 rounded-full",
                  isFull
                    ? "bg-amber-600 dark:bg-amber-500"
                    : "bg-primary"
                )}
                style={{ width: `${Math.min(enrollmentPercentage, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* CTA Button */}
        {showDetailsLink && (
          <div className="pt-2">
            <AcademicianButton
              variant="primary"
              className="w-full"
              disabled={status === "completed" || (status === "enrolling" && isFull)}
            >
              {isFull && status === "enrolling" ? "Waitlist" : ctaText}
            </AcademicianButton>
          </div>
        )}
      </div>
    </div>
  )

  // Wrap with Link if href is provided and details link is shown
  if (href && showDetailsLink) {
    return (
      <Link to={href} className="block">
        {cardContent}
      </Link>
    )
  }

  return cardContent
}